import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { authService } from "@/services/authService";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await authService.getMe();
      if (data.requires_password_change && window.location.pathname !== "/change-password") {
        setUser(data);
        setLoading(false);
        window.location.href = "/change-password";
        return;
      }
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await authService.login(email, password);
    if (!data.user?.requires_password_change) {
      setUser(data.user);
    }
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // Retry once on failure to ensure cookies are cleared server-side
      try { await authService.logout(); } catch { /* best effort */ }
    }
    setUser(null);
    // Force clear any cached auth state
    window.__authCleared = true;
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
  }, []);

  const contextValue = useMemo(() => ({ user, loading, login, logout, checkAuth, updateUser }), [user, loading, login, logout, checkAuth, updateUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
